import { Link } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const STEPS = [
  {
    number: '01',
    icon: 'ri-discuss-line',
    title: 'Understand',
    short: 'We start with a conversation, not a pitch — learning your business, goals, and current digital position.',
    accent: 'text-brand-blue',
    border: 'border-brand-blue/25',
    bg: 'bg-brand-blue/10',
  },
  {
    number: '02',
    icon: 'ri-map-2-line',
    title: 'Strategize',
    short: 'We build a clear plan: what channels to focus on, what to prioritize, and what outcomes to work towards.',
    accent: 'text-violet-500',
    border: 'border-violet-200',
    bg: 'bg-violet-50',
  },
  {
    number: '03',
    icon: 'ri-play-circle-line',
    title: 'Execute',
    short: 'We launch campaigns, create content, and manage platforms — staying hands-on throughout.',
    accent: 'text-orange-500',
    border: 'border-orange-200',
    bg: 'bg-orange-50',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Track & Improve',
    short: 'We monitor performance, share transparent reports, and refine continuously based on data.',
    accent: 'text-brand-blue-light',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
  },
];

function StepCard({ step, delay }: { step: typeof STEPS[0]; delay: number }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div
      ref={ref}
      className="flex flex-col"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
      }}
    >
      <div className="relative">
        <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${step.bg} border ${step.border} mb-5`}>
          <i className={`${step.icon} text-xl ${step.accent}`}></i>
        </div>
        <span className="absolute top-1 right-0 font-display font-bold text-4xl text-gray-100 leading-none select-none">
          {step.number}
        </span>
      </div>
      <span className={`text-[11px] font-bold uppercase tracking-widest ${step.accent} mb-2`}>
        Step {step.number}
      </span>
      <h3 className="font-display font-semibold text-gray-900 text-lg mb-2">{step.title}</h3>
      <p className="text-gray-600 text-[13px] leading-relaxed">{step.short}</p>
    </div>
  );
}

export default function ProcessPreview() {
  const { ref: headRef, visible: headVisible } = useInView(0.2);

  return (
    <section className="py-12 md:py-16 bg-gray-50 border-t border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={headRef}
          className="mb-8 md:mb-10"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-5">
            How We Work
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-[2.6rem] text-gray-900 leading-tight max-w-lg">
              A Clear Process,<br />Every Step of the Way
            </h2>
            <p className="text-gray-600 text-[15px] max-w-sm leading-relaxed">
              No guesswork, no surprises. We follow a structured approach so you always know where things stand.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-8">
          {STEPS.map((step, i) => (
            <StepCard key={step.number} step={step} delay={i * 80} />
          ))}
        </div>

        <div className="flex justify-center">
          <Link
            to="/process"
            className="btn-outline px-6 py-3"
          >
            See the Full Process
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
